import React, { useState, useRef, useEffect } from "react";
import { Mic, Send, Square } from "lucide-react";
import { uploadVoice } from "../services/api";

export default function InputBar({ onSend }) {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);

  // Real waveform
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationId = useRef(null);

  const handleSendText = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend({ type: "text", text: trimmed, self: true });
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      mediaRecorderRef.current.start();

      // Web Audio API for waveform
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;

      source.connect(analyserRef.current);

      setRecording(true);
      requestAnimationFrame(drawWaveform);
    } catch (error) {
      alert("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);

    cancelAnimationFrame(animationId.current);

    audioContextRef.current?.close();

    mediaRecorderRef.current.onstop = async () => {
      const blob = new Blob(audioChunks.current, { type: "audio/webm" });
      const localURL = URL.createObjectURL(blob);

      onSend({ type: "audio", blob:blob,url: localURL, self: true });

      // try {
      //   await uploadVoice(blob);
      // } catch (err) {
      //   console.error("Upload failed");
      // }
    };
  };

  // ---- REAL WAVEFORM DRAWING ----
  const drawWaveform = () => {
    animationId.current = requestAnimationFrame(drawWaveform);

    const canvas = canvasRef.current;
    if (!canvas) return; // ← FIX: stop drawing when canvas is unmounted
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const analyser = analyserRef.current;

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(buffer);

    canvas.width = canvas.offsetWidth;
    canvas.height = 60; // height of wave area

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = 2;
    ctx.strokeStyle = "#9b5cf1";

    ctx.beginPath();
    const sliceWidth = canvas.width / buffer.length;

    let x = 0;
    for (let i = 0; i < buffer.length; i++) {
      const v = buffer[i] / 128.0;
      const y = (v * canvas.height) / 2;

      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);

      x += sliceWidth;
    }

    ctx.lineTo(canvas.width, canvas.height / 2);
    ctx.stroke();
  };

  return (
    <div className=" border-t border-gray-200 bg-white/70 backdrop-blur-md">

      {/* WHEN RECORDING → full width waveform */}
      {recording ? (
        <div className="flex items-center gap-3 px-1">
          <button
            onClick={stopRecording}
            className="p-3 rounded-full bg-red-500 text-white"
          >
            <Square size={20} />
          </button>

          <canvas
            ref={canvasRef}
            className="w-full h-14 bg-purple-50  shadow-inner"
          />
           <button
            onClick={stopRecording}
            className="p-3 rounded-full bg-purple-500 text-white"
          >
            <Send size={18} />
          </button>
        </div>
      ) : (
        /* NORMAL INPUT BAR */
        <div className="p-3 bg-white/60 backdrop-blur-md flex items-center gap-3">
          {/* 🎤 Mic / Stop Recording Button */}
          <button
            onClick={startRecording}
            className={`p-2 rounded-full transition ${recording
                ? "bg-red-500 text-white animate-pulse"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            title={recording ? "Stop Recording" : "Start Voice Recording"}
          >
            {recording ? <Square size={18} /> : <Mic size={18} />}
          </button>

          {/* 📝 Text Input */}
          <textarea
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="flex-1 resize-none bg-white border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          />

          {/* 🚀 Send Button */}
          <button
            onClick={handleSendText}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-sm transition"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
